<script setup lang="ts">
import { ref, inject, computed, watch, onMounted, Ref } from 'vue'
import { useRoute } from 'vue-router'
import { toast } from 'vue-sonner'
import axios from 'axios'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Member } from '@/types/Member'
import { VhostInfo } from '@/types/Vhost'
import {
	Settings, Palette, LayoutGrid, Calendar,
	Tag, Info, Trophy, UserPlus, FileText, ListChecks,
	ChevronLeft, ChevronRight, Save, Loader2, Eye, Upload
} from 'lucide-vue-next'

const route = useRoute()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const vhostInfo = inject<Ref<VhostInfo> | null>('vhostInfo', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)
const appId = computed(() => route.params.appId as string || '')

const loading = ref(true)
const saving = ref(false)
const currentPage = ref('general')

// Wizard pages
const pages = [
	{ id: 'general', title: 'Setup', icon: Settings, description: 'Basic portal configuration' },
	{ id: 'look', title: 'Look & Feel', icon: Palette, description: 'Colors, fonts, and branding' },
	{ id: 'sections', title: 'Sections', icon: LayoutGrid, description: 'Portal page sections' },
	{ id: 'categories', title: 'Categories', icon: Tag, description: 'Video categories and tags' },
	{ id: 'details', title: 'Details', icon: Info, description: 'SEO and additional info' },
	{ id: 'upload', title: 'Upload Settings', icon: Upload, description: 'Upload form configuration' },
	{ id: 'registration', title: 'Registration', icon: UserPlus, description: 'User registration setup' },
	{ id: 'laststep', title: 'Publish', icon: ListChecks, description: 'Review and publish' },
]

const currentPageIndex = computed(() => pages.findIndex(p => p.id === currentPage.value))
const isFirstPage = computed(() => currentPageIndex.value === 0)
const isLastPage = computed(() => currentPageIndex.value === pages.length - 1)

const goNext = () => {
	if (!isLastPage.value) currentPage.value = pages[currentPageIndex.value + 1].id
}
const goPrev = () => {
	if (!isFirstPage.value) currentPage.value = pages[currentPageIndex.value - 1].id
}

// Portal settings model
const portal = ref({
	name: '',
	description: '',
	domain: '',
	language: 'en',
	primaryColor: '#3b82f6',
	highlightColor: '#f59e0b',
	font: 'Roboto',
	logoUrl: '',
	bgImageUrl: '',
	showNavbar: true,
	showFooter: true,
	showSearch: true,
	showFilters: true,
	showUploadButton: true,
	enableRegistration: true,
	requireApproval: false,
	maxUploadSize: 500,
	allowedFormats: 'mp4,mov,avi,webm',
	categories: [] as { name: string; slug: string }[],
	metaTitle: '',
	metaDescription: '',
	metaKeywords: '',
	status: 'draft',
})

const newCategoryName = ref('')

const addCategory = () => {
	if (!newCategoryName.value.trim()) return
	portal.value.categories.push({
		name: newCategoryName.value.trim(),
		slug: newCategoryName.value.trim().toLowerCase().replace(/\s+/g, '-'),
	})
	newCategoryName.value = ''
}

const removeCategory = (idx: number) => {
	portal.value.categories.splice(idx, 1)
}

const savePortal = async () => {
	saving.value = true
	try {
		// Save portal settings via API
		await axios.post('/services/json', {
			method: 'vhost.updateApplicationSettings',
			appId: appId.value || 0,
			vhostID: vhostId?.value,
			settings: portal.value,
		})
		toast.success('Video portal saved')
	} catch (err) {
		toast.error('Failed to save portal settings')
	} finally {
		saving.value = false
	}
}

const publishPortal = async () => {
	portal.value.status = 'active'
	await savePortal()
	toast.success('Video portal published!')
}

const fetchPortalConfig = async () => {
	loading.value = true
	try {
		if (appId.value) {
			const { data } = await axios.post('/services/json', {
				method: 'vhost.getApplicationSettings',
				appId: appId.value,
				vhostID: vhostId?.value,
			})
			if (data.status && data.result) {
				Object.assign(portal.value, data.result)
			}
		}
	} catch (err) {
		console.error('Failed to load portal config:', err)
	} finally {
		loading.value = false
	}
}

onMounted(fetchPortalConfig)
</script>

<template>
	<div class="flex flex-col h-full">
		<!-- Top navigation tabs — Desktop horizontal, Mobile select -->
		<div class="bg-background border-b sticky top-0 z-40">
			<!-- Desktop: horizontal tabs -->
			<div class="hidden md:flex items-center gap-1 px-4 py-2 overflow-x-auto">
				<button
					v-for="page in pages"
					:key="page.id"
					@click="currentPage = page.id"
					class="flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors"
					:class="currentPage === page.id
						? 'bg-primary text-primary-foreground'
						: 'text-muted-foreground hover:bg-muted hover:text-foreground'"
				>
					<component :is="page.icon" class="h-4 w-4" />
					{{ page.title }}
				</button>
				<div class="flex-1" />
				<Button variant="outline" size="sm" class="gap-2" @click="savePortal" :disabled="saving">
					<Save class="h-4 w-4" />
					{{ saving ? 'Saving...' : 'Save' }}
				</Button>
			</div>
			<!-- Mobile: select + save -->
			<div class="md:hidden flex items-center gap-2 px-3 py-2">
				<Select :model-value="currentPage" @update:model-value="(v: string) => currentPage = v">
					<SelectTrigger class="flex-1">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem v-for="page in pages" :key="page.id" :value="page.id">
							{{ page.title }}
						</SelectItem>
					</SelectContent>
				</Select>
				<Button variant="outline" size="icon" @click="savePortal" :disabled="saving">
					<Save class="h-4 w-4" />
				</Button>
			</div>
		</div>

		<!-- Loading -->
		<div v-if="loading" class="flex-1 p-6 space-y-4">
			<Skeleton class="h-8 w-64" />
			<Skeleton class="h-4 w-96" />
			<div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
				<Skeleton class="h-20" />
				<Skeleton class="h-20" />
				<Skeleton class="h-20" />
				<Skeleton class="h-20" />
			</div>
		</div>

		<!-- Content -->
		<div v-else class="flex-1 overflow-y-auto p-4 md:p-6 max-w-4xl">

			<!-- GENERAL / SETUP -->
			<div v-if="currentPage === 'general'" class="space-y-6">
				<div>
					<h2 class="text-xl font-semibold">Setup</h2>
					<p class="text-sm text-muted-foreground">Configure the basic settings for your video portal.</p>
				</div>
				<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div class="space-y-2">
						<Label>Portal Name</Label>
						<Input v-model="portal.name" placeholder="My Video Portal" />
					</div>
					<div class="space-y-2">
						<Label>Domain</Label>
						<Input v-model="portal.domain" placeholder="videos.example.com" />
					</div>
				</div>
				<div class="space-y-2">
					<Label>Description</Label>
					<Textarea v-model="portal.description" placeholder="A brief description of your video portal..." rows="3" />
				</div>
				<div class="space-y-2">
					<Label>Language</Label>
					<Select v-model="portal.language">
						<SelectTrigger class="max-w-xs">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="en">English</SelectItem>
							<SelectItem value="fr">French</SelectItem>
							<SelectItem value="es">Spanish</SelectItem>
							<SelectItem value="de">German</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			<!-- LOOK & FEEL -->
			<div v-if="currentPage === 'look'" class="space-y-6">
				<div>
					<h2 class="text-xl font-semibold">Look & Feel</h2>
					<p class="text-sm text-muted-foreground">Customize the appearance and branding of your portal.</p>
				</div>
				<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div class="space-y-2">
						<Label>Logo URL</Label>
						<Input v-model="portal.logoUrl" placeholder="https://..." />
						<p class="text-xs text-muted-foreground">Suggested: 600x200px. PNG, JPG, or WebP.</p>
					</div>
					<div class="space-y-2">
						<Label>Background Image URL</Label>
						<Input v-model="portal.bgImageUrl" placeholder="https://..." />
						<p class="text-xs text-muted-foreground">Suggested: 1920x1080px.</p>
					</div>
				</div>
				<Separator />
				<div class="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div class="space-y-2">
						<Label>Primary Color</Label>
						<div class="flex items-center gap-2">
							<input type="color" v-model="portal.primaryColor" class="h-10 w-10 rounded border cursor-pointer" />
							<Input v-model="portal.primaryColor" class="flex-1" />
						</div>
					</div>
					<div class="space-y-2">
						<Label>Highlight Color</Label>
						<div class="flex items-center gap-2">
							<input type="color" v-model="portal.highlightColor" class="h-10 w-10 rounded border cursor-pointer" />
							<Input v-model="portal.highlightColor" class="flex-1" />
						</div>
					</div>
					<div class="space-y-2">
						<Label>Font</Label>
						<Select v-model="portal.font">
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Roboto">Roboto</SelectItem>
								<SelectItem value="Montserrat">Montserrat</SelectItem>
								<SelectItem value="Lato">Lato</SelectItem>
								<SelectItem value="Ubuntu">Ubuntu</SelectItem>
								<SelectItem value="Inter">Inter</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
			</div>

			<!-- SECTIONS -->
			<div v-if="currentPage === 'sections'" class="space-y-6">
				<div>
					<h2 class="text-xl font-semibold">Sections</h2>
					<p class="text-sm text-muted-foreground">Toggle which sections appear on your portal.</p>
				</div>
				<div class="space-y-4 max-w-lg">
					<div class="flex items-center justify-between">
						<div>
							<p class="font-medium text-sm">Navigation Bar</p>
							<p class="text-xs text-muted-foreground">Show the top navigation bar</p>
						</div>
						<Switch v-model:checked="portal.showNavbar" />
					</div>
					<Separator />
					<div class="flex items-center justify-between">
						<div>
							<p class="font-medium text-sm">Search Bar</p>
							<p class="text-xs text-muted-foreground">Allow visitors to search content</p>
						</div>
						<Switch v-model:checked="portal.showSearch" />
					</div>
					<Separator />
					<div class="flex items-center justify-between">
						<div>
							<p class="font-medium text-sm">Category Filters</p>
							<p class="text-xs text-muted-foreground">Show category filter sidebar/buttons</p>
						</div>
						<Switch v-model:checked="portal.showFilters" />
					</div>
					<Separator />
					<div class="flex items-center justify-between">
						<div>
							<p class="font-medium text-sm">Upload Button</p>
							<p class="text-xs text-muted-foreground">Show a public upload button</p>
						</div>
						<Switch v-model:checked="portal.showUploadButton" />
					</div>
					<Separator />
					<div class="flex items-center justify-between">
						<div>
							<p class="font-medium text-sm">Footer</p>
							<p class="text-xs text-muted-foreground">Show the footer section</p>
						</div>
						<Switch v-model:checked="portal.showFooter" />
					</div>
				</div>
			</div>

			<!-- CATEGORIES -->
			<div v-if="currentPage === 'categories'" class="space-y-6">
				<div>
					<h2 class="text-xl font-semibold">Categories</h2>
					<p class="text-sm text-muted-foreground">Define content categories for your video portal.</p>
				</div>
				<div class="flex gap-2 max-w-md">
					<Input v-model="newCategoryName" placeholder="Category name" @keyup.enter="addCategory" />
					<Button @click="addCategory" :disabled="!newCategoryName.trim()">Add</Button>
				</div>
				<div v-if="portal.categories.length > 0" class="flex flex-wrap gap-2">
					<Badge
						v-for="(cat, idx) in portal.categories"
						:key="idx"
						variant="secondary"
						class="gap-1 text-sm py-1.5 px-3"
					>
						{{ cat.name }}
						<button @click="removeCategory(idx)" class="ml-1 text-muted-foreground hover:text-destructive">&times;</button>
					</Badge>
				</div>
				<div v-else class="text-sm text-muted-foreground py-8 text-center">
					No categories added yet. Add categories to help visitors find content.
				</div>
			</div>

			<!-- DETAILS -->
			<div v-if="currentPage === 'details'" class="space-y-6">
				<div>
					<h2 class="text-xl font-semibold">Details & SEO</h2>
					<p class="text-sm text-muted-foreground">Set metadata for search engine optimization.</p>
				</div>
				<div class="grid grid-cols-1 gap-4 max-w-lg">
					<div class="space-y-2">
						<Label>Meta Title</Label>
						<Input v-model="portal.metaTitle" placeholder="Video Portal - Your Brand" />
					</div>
					<div class="space-y-2">
						<Label>Meta Description</Label>
						<Textarea v-model="portal.metaDescription" placeholder="Describe your video portal..." rows="3" />
					</div>
					<div class="space-y-2">
						<Label>Meta Keywords</Label>
						<Input v-model="portal.metaKeywords" placeholder="videos, media, content" />
					</div>
				</div>
			</div>

			<!-- UPLOAD SETTINGS -->
			<div v-if="currentPage === 'upload'" class="space-y-6">
				<div>
					<h2 class="text-xl font-semibold">Upload Settings</h2>
					<p class="text-sm text-muted-foreground">Configure how users upload content to your portal.</p>
				</div>
				<div class="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
					<div class="space-y-2">
						<Label>Max Upload Size (MB)</Label>
						<Input v-model.number="portal.maxUploadSize" type="number" />
					</div>
					<div class="space-y-2">
						<Label>Allowed Formats</Label>
						<Input v-model="portal.allowedFormats" placeholder="mp4,mov,avi,webm" />
						<p class="text-xs text-muted-foreground">Comma-separated file extensions</p>
					</div>
				</div>
				<div class="flex items-center justify-between max-w-lg">
					<div>
						<p class="font-medium text-sm">Require Moderation</p>
						<p class="text-xs text-muted-foreground">Uploads require admin approval before going live</p>
					</div>
					<Switch v-model:checked="portal.requireApproval" />
				</div>
			</div>

			<!-- REGISTRATION -->
			<div v-if="currentPage === 'registration'" class="space-y-6">
				<div>
					<h2 class="text-xl font-semibold">Registration</h2>
					<p class="text-sm text-muted-foreground">Configure user registration for your portal.</p>
				</div>
				<div class="flex items-center justify-between max-w-lg">
					<div>
						<p class="font-medium text-sm">Enable Registration</p>
						<p class="text-xs text-muted-foreground">Allow visitors to create accounts</p>
					</div>
					<Switch v-model:checked="portal.enableRegistration" />
				</div>
			</div>

			<!-- PUBLISH / LAST STEP -->
			<div v-if="currentPage === 'laststep'" class="space-y-6">
				<div>
					<h2 class="text-xl font-semibold">Review & Publish</h2>
					<p class="text-sm text-muted-foreground">Review your portal settings and go live.</p>
				</div>
				<Card>
					<CardContent class="p-6 space-y-4">
						<div class="grid grid-cols-2 gap-y-3 text-sm">
							<span class="text-muted-foreground">Portal Name</span>
							<span class="font-medium">{{ portal.name || '—' }}</span>
							<span class="text-muted-foreground">Domain</span>
							<span class="font-medium">{{ portal.domain || '—' }}</span>
							<span class="text-muted-foreground">Status</span>
							<span>
								<Badge :variant="portal.status === 'active' ? 'default' : 'secondary'">
									{{ portal.status === 'active' ? 'Published' : 'Draft' }}
								</Badge>
							</span>
							<span class="text-muted-foreground">Categories</span>
							<span class="font-medium">{{ portal.categories.length }} configured</span>
							<span class="text-muted-foreground">Registration</span>
							<span class="font-medium">{{ portal.enableRegistration ? 'Enabled' : 'Disabled' }}</span>
						</div>
					</CardContent>
				</Card>
				<div class="flex gap-3">
					<Button @click="savePortal" variant="outline" :disabled="saving" class="gap-2">
						<Save class="h-4 w-4" /> Save as Draft
					</Button>
					<Button @click="publishPortal" :disabled="saving" class="gap-2">
						<ListChecks class="h-4 w-4" /> Publish Portal
					</Button>
				</div>
			</div>
		</div>

		<!-- Bottom navigation — prev/next -->
		<div class="border-t bg-background px-4 py-3 flex items-center justify-between">
			<Button variant="outline" size="sm" @click="goPrev" :disabled="isFirstPage" class="gap-2">
				<ChevronLeft class="h-4 w-4" /> Previous
			</Button>
			<span class="text-xs text-muted-foreground">
				Step {{ currentPageIndex + 1 }} of {{ pages.length }}
			</span>
			<Button variant="outline" size="sm" @click="goNext" :disabled="isLastPage" class="gap-2">
				Next <ChevronRight class="h-4 w-4" />
			</Button>
		</div>
	</div>
</template>
